/**
 * F-17-marks: the bold / strike / ink / highlight control group, shared by
 * every surface that formats part of a cell's text.
 *
 * Two of them now — the script view's highlight menu (F-28) and the grid cell
 * editor's own bar (F-12) — and they are deliberately the SAME component
 * rather than two that look alike: the marks they write are one data model on
 * one cell, so a writer who bolds a phrase in the grid and re-colors it in the
 * script must find the button where they left it, showing the state the other
 * view just wrote.
 *
 * Every control acts on POINTERDOWN with `preventDefault`, never on click. A
 * click would first move focus out of the textarea holding the selection, and
 * the browser drops the text selection on the way — taking the range the
 * button was about to act on with it.
 */
import { useState } from "react";

// One palette across the app: the F-27 toolbar's presets ARE the presets a
// text range offers, so a phrase highlighted here and a cell filled there use
// the same eight colors (features/selection-toolbar/palette.ts).
import { themedFill } from "../core/color.js";
import { useThemeColors } from "../core/useThemeColors.js";
import { BG_PRESETS, TEXT_PRESETS } from "../features/selection-toolbar/palette.js";
import type { TextFormatState } from "./text-format.js";

/** The live state of the range plus the two writes a bar can make. */
export interface TextFormatActions extends TextFormatState {
  onToggle: (kind: "bold" | "strike") => void;
  /** `undefined` clears the range — "Automatic" ink / "No highlight". */
  onColor: (kind: "color" | "hl", color: string | undefined) => void;
}

/**
 * A swatch button with a preset popover — the F-27 toolbar's palette, shrunk
 * to what a text range needs (presets and a clear, no opacity slider and no
 * custom hex: this bar floats over the line the writer is reading, and a text
 * field in it would take the focus the whole bar exists to keep).
 */
function MarkPaletteButton({
  kind,
  label,
  open,
  onOpen,
  current,
  presets,
  clearLabel,
  onPick,
}: {
  kind: "color" | "hl";
  label: string;
  open: boolean;
  onOpen: () => void;
  current: string | undefined;
  presets: readonly string[];
  clearLabel: string;
  onPick: (color: string | undefined) => void;
}) {
  const { surface } = useThemeColors();
  // A HIGHLIGHT swatch previews what will actually be painted, which in dark
  // mode is the re-rendered fill (F-122, core/color.ts `themedFill`) — a
  // picker that showed the stored pastel would be advertising a color the
  // board never displays. Ink presets are NOT re-rendered: they are saturated
  // by design and each clears AA on both surfaces already.
  const shown = (color: string): string =>
    kind === "hl" ? (themedFill(color, surface) ?? color) : color;
  return (
    <span className="tfmt-palette">
      <button
        type="button"
        className={`tfmt-btn${open ? " on" : ""}`}
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        data-tip={label}
        data-tip-pos="top"
        onPointerDown={(event) => {
          event.preventDefault();
          onOpen();
        }}
      >
        {/* Both read "A", the way every editor writes these two: the INK one
            carries its color in a bar under the letter, the HIGHLIGHT one
            behind it — which is exactly what each does to the text. */}
        <span
          className={`tfmt-glyph ${kind}${kind === "hl" && current !== undefined ? " on" : ""}`}
          style={
            kind === "hl" && current !== undefined ? { background: shown(current) } : undefined
          }
        >
          A
        </span>
        {kind === "color" && (
          <span
            className="tfmt-bar"
            style={current === undefined ? undefined : { background: shown(current) }}
          />
        )}
      </button>
      {open && (
        <div className="tfmt-pop" role="menu" aria-label={label}>
          <div className="tfmt-swatches">
            {presets.map((color) => (
              <button
                key={color}
                type="button"
                role="menuitemradio"
                aria-checked={current === color}
                aria-label={color}
                className={`tfmt-swatch${current === color ? " on" : ""}`}
                style={{ background: shown(color) }}
                onPointerDown={(event) => {
                  event.preventDefault();
                  onPick(color);
                }}
              />
            ))}
          </div>
          <button
            type="button"
            role="menuitem"
            className="tfmt-clear"
            onPointerDown={(event) => {
              event.preventDefault();
              onPick(undefined);
            }}
          >
            {clearLabel}
          </button>
        </div>
      )}
    </span>
  );
}

/**
 * The four controls, in the order every editor puts them. Rendered inside
 * whatever bar the surface provides — this owns no positioning, only which
 * popover is open (at most one, like the F-27 toolbar's).
 */
export function TextFormatControls({ format }: { format: TextFormatActions }) {
  const [palette, setPalette] = useState<"color" | "hl" | null>(null);
  return (
    <>
      <button
        type="button"
        className={`tfmt-btn${format.bold ? " on" : ""}`}
        role="menuitemcheckbox"
        aria-checked={format.bold}
        data-tip="Bold (⌘B)"
        data-tip-pos="top"
        onPointerDown={(event) => {
          event.preventDefault();
          setPalette(null);
          format.onToggle("bold");
        }}
      >
        <span className="tfmt-b">B</span>
      </button>
      <button
        type="button"
        className={`tfmt-btn${format.strike ? " on" : ""}`}
        role="menuitemcheckbox"
        aria-checked={format.strike}
        data-tip="Strikethrough (⌘⇧X)"
        data-tip-pos="top"
        onPointerDown={(event) => {
          event.preventDefault();
          setPalette(null);
          format.onToggle("strike");
        }}
      >
        <span className="tfmt-s">S</span>
      </button>
      <MarkPaletteButton
        kind="color"
        label="Text color"
        open={palette === "color"}
        onOpen={() => setPalette(palette === "color" ? null : "color")}
        current={format.color}
        presets={TEXT_PRESETS}
        clearLabel="Automatic"
        onPick={(color) => {
          setPalette(null);
          format.onColor("color", color);
        }}
      />
      <MarkPaletteButton
        kind="hl"
        label="Highlight"
        open={palette === "hl"}
        onOpen={() => setPalette(palette === "hl" ? null : "hl")}
        current={format.hl}
        presets={BG_PRESETS}
        clearLabel="No highlight"
        onPick={(color) => {
          setPalette(null);
          format.onColor("hl", color);
        }}
      />
    </>
  );
}
